// Exports the configured panel as real vector geometry for laser software.
//
// html-to-image's toSvg() wraps a serialised copy of the DOM inside a single
// <foreignObject>, so all of the positioning still lives in CSS. EZCAD (and
// CorelDRAW, Illustrator, ...) has no HTML layout engine, so it throws the
// wrapper away and drops every shape at the origin - which is why those files
// arrive with all the artwork stacked in one place.
//
// So instead of screenshotting the DOM we measure it. Every icon dropped onto
// the panel is injected as real inline SVG, so its outlines are flattened into
// polylines expressed in the panel's own millimetre coordinate system and
// written out as a plain SVG (no groups, no transforms) and as a DXF.

const SVG_NS = "http://www.w3.org/2000/svg";
const PX_TO_MM = 25.4 / 96; // CSS defines 1in as exactly 96px
const FLATTEN_MM = 0.05; // curve sampling resolution
const SIMPLIFY_MM = 0.01; // well under any laser spot size
const MAX_SAMPLES = 20000; // guard against pathological path lengths

const GEOMETRY_TAGS = [
  "path",
  "rect",
  "circle",
  "ellipse",
  "line",
  "polyline",
  "polygon",
];

const num = (el, attr, fallback = 0) => {
  const value = parseFloat(el.getAttribute(attr));
  return Number.isFinite(value) ? value : fallback;
};

const fmt = (value) => {
  const rounded = Math.round(value * 1000) / 1000;
  return Object.is(rounded, -0) ? "0" : String(rounded);
};

// Turn the basic shapes into path data so a single flattening routine covers
// every element type. Safari only implements getTotalLength() on <path>.
const shapeToPathData = (el) => {
  const tag = el.tagName.toLowerCase();

  if (tag === "path") {
    return el.getAttribute("d");
  }

  if (tag === "rect") {
    const x = num(el, "x");
    const y = num(el, "y");
    const w = num(el, "width");
    const h = num(el, "height");
    if (w <= 0 || h <= 0) return null;

    let rx = parseFloat(el.getAttribute("rx"));
    let ry = parseFloat(el.getAttribute("ry"));
    rx = Number.isFinite(rx) ? rx : Number.isFinite(ry) ? ry : 0;
    ry = Number.isFinite(ry) ? ry : rx;
    rx = Math.min(rx, w / 2);
    ry = Math.min(ry, h / 2);

    if (rx <= 0 || ry <= 0) {
      return `M ${x} ${y} H ${x + w} V ${y + h} H ${x} Z`;
    }
    return (
      `M ${x + rx} ${y} H ${x + w - rx}` +
      ` A ${rx} ${ry} 0 0 1 ${x + w} ${y + ry}` +
      ` V ${y + h - ry}` +
      ` A ${rx} ${ry} 0 0 1 ${x + w - rx} ${y + h}` +
      ` H ${x + rx}` +
      ` A ${rx} ${ry} 0 0 1 ${x} ${y + h - ry}` +
      ` V ${y + ry}` +
      ` A ${rx} ${ry} 0 0 1 ${x + rx} ${y} Z`
    );
  }

  if (tag === "circle" || tag === "ellipse") {
    const cx = num(el, "cx");
    const cy = num(el, "cy");
    const rx = tag === "circle" ? num(el, "r") : num(el, "rx");
    const ry = tag === "circle" ? num(el, "r") : num(el, "ry");
    if (rx <= 0 || ry <= 0) return null;
    return (
      `M ${cx - rx} ${cy}` +
      ` A ${rx} ${ry} 0 0 1 ${cx + rx} ${cy}` +
      ` A ${rx} ${ry} 0 0 1 ${cx - rx} ${cy} Z`
    );
  }

  if (tag === "line") {
    return `M ${num(el, "x1")} ${num(el, "y1")} L ${num(el, "x2")} ${num(
      el,
      "y2"
    )}`;
  }

  if (tag === "polyline" || tag === "polygon") {
    const points = (el.getAttribute("points") || "")
      .trim()
      .split(/[\s,]+/)
      .map(Number)
      .filter(Number.isFinite);
    if (points.length < 4) return null;

    let d = `M ${points[0]} ${points[1]}`;
    for (let i = 2; i + 1 < points.length; i += 2) {
      d += ` L ${points[i]} ${points[i + 1]}`;
    }
    return tag === "polygon" ? `${d} Z` : d;
  }

  return null;
};

// Uniform sampling turns a straight edge into hundreds of collinear vertices,
// which bloats the file and slows the marking software down. Douglas-Peucker
// drops every point that sits within SIMPLIFY_MM of the line it lies on.
const simplifyPolyline = (points, tolerance = SIMPLIFY_MM) => {
  if (points.length < 3) return points;

  const keep = new Array(points.length).fill(false);
  keep[0] = true;
  keep[points.length - 1] = true;

  const stack = [[0, points.length - 1]];
  while (stack.length) {
    const [start, end] = stack.pop();
    if (end - start < 2) continue;

    const [ax, ay] = points[start];
    const [bx, by] = points[end];
    const dx = bx - ax;
    const dy = by - ay;
    const span = Math.hypot(dx, dy);

    let worst = -1;
    let worstIndex = -1;
    for (let i = start + 1; i < end; i += 1) {
      const [px, py] = points[i];
      const distance =
        span === 0
          ? Math.hypot(px - ax, py - ay)
          : Math.abs(dy * px - dx * py + bx * ay - by * ax) / span;
      if (distance > worst) {
        worst = distance;
        worstIndex = i;
      }
    }

    if (worst > tolerance && worstIndex > start) {
      keep[worstIndex] = true;
      stack.push([start, worstIndex], [worstIndex, end]);
    }
  }

  return points.filter((_, index) => keep[index]);
};

// A CSS colour string as the exporter needs it: a hex triplet plus its alpha,
// or null when there is nothing to paint.
const cssColour = (value) => {
  if (!value || value === "none" || value === "transparent") return null;
  const match = /rgba?\(([^)]+)\)/.exec(value);
  if (!match) return /^#[0-9a-f]{3,8}$/i.test(value) ? { hex: value, alpha: 1 } : null;

  const parts = match[1].split(",").map((part) => parseFloat(part));
  const alpha = parts.length > 3 ? parts[3] : 1;
  if (!(alpha > 0)) return null;

  const hex = `#${parts
    .slice(0, 3)
    .map((channel) => Math.round(channel).toString(16).padStart(2, "0"))
    .join("")}`;
  return { hex, alpha };
};

// Walk a geometry element, sampling it into subpaths in panel-millimetre
// space. getPointAtLength() jumps discontinuously between subpaths, which is
// how a path made of several contours gets split back apart - but they stay
// grouped in one shape so fill rules (glyph holes) still work.
const flattenElement = (el, toPanelMm) => {
  const pathData = shapeToPathData(el);
  if (!pathData || !el.parentNode) return null;

  const probe = document.createElementNS(SVG_NS, "path");
  probe.setAttribute("d", pathData);
  const transform = el.getAttribute("transform");
  if (transform) probe.setAttribute("transform", transform);
  el.parentNode.insertBefore(probe, el);

  try {
    const ctm = probe.getScreenCTM();
    const totalLength = probe.getTotalLength();
    if (!ctm || !Number.isFinite(totalLength) || totalLength <= 0) return null;

    // mm covered by one user unit, used to pick a sampling step that is fine
    // enough in the output rather than in the icon's own coordinate system.
    const mmPerUnit =
      Math.sqrt(Math.abs(ctm.a * ctm.d - ctm.b * ctm.c)) * PX_TO_MM;
    const stepUnits = mmPerUnit > 0 ? FLATTEN_MM / mmPerUnit : totalLength / 200;
    const samples = Math.min(
      MAX_SAMPLES,
      Math.max(2, Math.ceil(totalLength / stepUnits))
    );
    const stepMm = (totalLength / samples) * mmPerUnit;
    const breakMm = Math.max(stepMm * 4, 0.15);
    const weldMm = Math.max(stepMm * 1.5, 0.02);

    const subpaths = [];
    let current = [];

    const flush = () => {
      if (current.length < 2) {
        current = [];
        return;
      }
      const first = current[0];
      const last = current[current.length - 1];
      const closed = Math.hypot(last[0] - first[0], last[1] - first[1]) <= weldMm;
      if (closed) current.pop();
      const simplified = simplifyPolyline(current);
      if (simplified.length >= 2) subpaths.push({ points: simplified, closed });
      current = [];
    };

    let previous = null;
    for (let i = 0; i <= samples; i += 1) {
      const raw = probe.getPointAtLength((totalLength * i) / samples);
      const point = toPanelMm(
        ctm.a * raw.x + ctm.c * raw.y + ctm.e,
        ctm.b * raw.x + ctm.d * raw.y + ctm.f
      );

      if (previous) {
        const distance = Math.hypot(
          point[0] - previous[0],
          point[1] - previous[1]
        );
        if (distance > breakMm) {
          // Gap between two subpaths - start a new contour rather than
          // drawing a line the laser would actually cut.
          flush();
          previous = null;
        } else if (distance <= weldMm / 4) {
          continue; // duplicate sample
        }
      }

      current.push(point);
      previous = point;
    }
    flush();
    if (!subpaths.length) return null;

    // Carry the icon's own colours through so the export looks like the panel.
    const style = window.getComputedStyle(el);
    const fill = cssColour(style.getPropertyValue("fill"));
    const stroke = cssColour(style.getPropertyValue("stroke"));
    const fillOpacity = parseFloat(style.getPropertyValue("fill-opacity"));
    const strokeWidth =
      (parseFloat(style.getPropertyValue("stroke-width")) || 0) * mmPerUnit;

    return {
      subpaths,
      fill: fillOpacity === 0 ? null : fill,
      fillOpacity: Number.isFinite(fillOpacity) ? fillOpacity : 1,
      fillRule: style.getPropertyValue("fill-rule") || "nonzero",
      stroke: strokeWidth > 0 ? stroke : null,
      strokeWidth,
    };
  } finally {
    el.parentNode.removeChild(probe);
  }
};

const parseBackgroundUrl = (value) => {
  const match = /url\((['"]?)(.*?)\1\)/.exec(value || "");
  return match ? match[2] : null;
};

const naturalSvgSize = (svgEl) => {
  const viewBox = (svgEl.getAttribute("viewBox") || "")
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  if (viewBox.length === 4 && viewBox[2] > 0 && viewBox[3] > 0) {
    return { width: viewBox[2], height: viewBox[3] };
  }
  const width = parseFloat(svgEl.getAttribute("width"));
  const height = parseFloat(svgEl.getAttribute("height"));
  if (Number.isFinite(width) && Number.isFinite(height)) {
    return { width, height };
  }
  return null;
};

// Some accessories (the 6A socket) are drawn with a CSS background-image that
// points at a vector file, so it is pulled in and positioned the way the
// browser lays a background out. Raster backgrounds are counted and reported -
// there is no vector geometry in a PNG to hand to the laser.
const injectVectorBackgrounds = async (panelNode) => {
  const cleanups = [];
  let rasterCount = 0;

  const elements = Array.from(panelNode.querySelectorAll("*"));
  for (const el of elements) {
    const url = parseBackgroundUrl(
      window.getComputedStyle(el).getPropertyValue("background-image")
    );
    if (!url) continue;

    if (!/\.svg(\?|#|$)/i.test(url)) {
      rasterCount += 1;
      continue;
    }

    let markup;
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      markup = await response.text();
    } catch (error) {
      console.warn("Could not load background artwork", url, error);
      continue;
    }

    const parsed = new DOMParser().parseFromString(markup, "image/svg+xml");
    const source = parsed.documentElement;
    if (!source || source.tagName.toLowerCase() !== "svg") continue;

    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) continue;

    const natural = naturalSvgSize(source) || {
      width: rect.width,
      height: rect.height,
    };
    const mode = window
      .getComputedStyle(el)
      .getPropertyValue("background-size")
      .trim();
    const scale =
      mode === "cover"
        ? Math.max(rect.width / natural.width, rect.height / natural.height)
        : Math.min(rect.width / natural.width, rect.height / natural.height);
    const width = natural.width * scale;
    const height = natural.height * scale;

    const holder = document.createElement("div");
    holder.dataset.laserExportTemp = "true";
    holder.style.cssText = [
      "position:absolute",
      `left:${(rect.width - width) / 2}px`,
      `top:${(rect.height - height) / 2}px`,
      `width:${width}px`,
      `height:${height}px`,
      "pointer-events:none",
      "visibility:hidden",
    ].join(";");

    const svgEl = document.importNode(source, true);
    svgEl.setAttribute("width", String(width));
    svgEl.setAttribute("height", String(height));
    if (!svgEl.getAttribute("viewBox")) {
      svgEl.setAttribute("viewBox", `0 0 ${natural.width} ${natural.height}`);
    }
    holder.appendChild(svgEl);

    const position = window.getComputedStyle(el).getPropertyValue("position");
    const inlinePosition = el.style.position;
    if (position === "static") el.style.position = "relative";
    el.appendChild(holder);

    cleanups.push(() => {
      el.removeChild(holder);
      el.style.position = inlinePosition;
    });
  }

  return { cleanups, rasterCount };
};

// --- Outlines --------------------------------------------------------------
// The panel edge and the module/button boxes are CSS borders rather than SVG,
// so they are rebuilt from the box model: border-box rectangle plus whatever
// border-radius the browser resolved.

const arcPoints = (cx, cy, rx, ry, from, to) => {
  const sweep = Math.abs(to - from);
  const length = ((rx + ry) / 2) * sweep; // close enough to pick a step count
  const steps = Math.max(4, Math.min(360, Math.ceil(length / FLATTEN_MM)));
  const points = [];
  for (let i = 0; i <= steps; i += 1) {
    const angle = from + ((to - from) * i) / steps;
    points.push([cx + rx * Math.cos(angle), cy + ry * Math.sin(angle)]);
  }
  return points;
};

const roundedRectPoints = (x, y, w, h, corners) => {
  const [tl, tr, br, bl] = corners;

  // CSS shrinks radii proportionally when neighbouring ones would overlap.
  const factor = Math.min(
    tl.h + tr.h > 0 ? w / (tl.h + tr.h) : Infinity,
    bl.h + br.h > 0 ? w / (bl.h + br.h) : Infinity,
    tl.v + bl.v > 0 ? h / (tl.v + bl.v) : Infinity,
    tr.v + br.v > 0 ? h / (tr.v + br.v) : Infinity,
    1
  );
  const scaled = corners.map((corner) => ({
    h: corner.h * factor,
    v: corner.v * factor,
  }));
  const [a, b, c, d] = scaled;

  const HALF_PI = Math.PI / 2;
  const points = [[x + a.h, y]];
  points.push([x + w - b.h, y]);
  if (b.h > 0 || b.v > 0) {
    points.push(...arcPoints(x + w - b.h, y + b.v, b.h, b.v, -HALF_PI, 0));
  }
  points.push([x + w, y + h - c.v]);
  if (c.h > 0 || c.v > 0) {
    points.push(...arcPoints(x + w - c.h, y + h - c.v, c.h, c.v, 0, HALF_PI));
  }
  points.push([x + d.h, y + h]);
  if (d.h > 0 || d.v > 0) {
    points.push(...arcPoints(x + d.h, y + h - d.v, d.h, d.v, HALF_PI, Math.PI));
  }
  points.push([x, y + a.v]);
  if (a.h > 0 || a.v > 0) {
    points.push(...arcPoints(x + a.h, y + a.v, a.h, a.v, Math.PI, 3 * HALF_PI));
  }

  // Drop the duplicates the corner joins leave behind.
  return points.filter((point, index) => {
    if (index === 0) return true;
    const previous = points[index - 1];
    return Math.hypot(point[0] - previous[0], point[1] - previous[1]) > 1e-6;
  });
};

const resolveRadius = (value, width, height) => {
  const parts = String(value || "0px").trim().split(/\s+/);
  const toMm = (raw, extent) => {
    if (raw.endsWith("%")) return (parseFloat(raw) / 100) * extent;
    return (parseFloat(raw) || 0) * PX_TO_MM;
  };
  return {
    h: toMm(parts[0], width),
    v: toMm(parts[1] || parts[0], height),
  };
};

// The border as one uniform stroke, or null when there is nothing to draw.
const borderStroke = (style) => {
  const sides = ["top", "right", "bottom", "left"];
  const widths = sides.map(
    (side) => parseFloat(style.getPropertyValue(`border-${side}-width`)) || 0
  );
  const drawn = sides.every((side, index) => {
    const lineStyle = style.getPropertyValue(`border-${side}-style`);
    return widths[index] > 0 && lineStyle !== "none" && lineStyle !== "hidden";
  });
  if (!drawn) return null;

  const colour = cssColour(style.getPropertyValue("border-top-color"));
  if (!colour) return null;

  return { colour, widthMm: Math.max(...widths) * PX_TO_MM };
};

// Rebuild an element's visible box - background fill and border - as a closed
// contour in panel millimetres.
const outlineForElement = (el, panelRect, { force = false, outerEdge = false } = {}) => {
  const style = window.getComputedStyle(el);
  if (
    style.getPropertyValue("display") === "none" ||
    style.getPropertyValue("visibility") === "hidden" ||
    parseFloat(style.getPropertyValue("opacity")) === 0
  ) {
    return null;
  }

  const fill = cssColour(style.getPropertyValue("background-color"));
  const border = borderStroke(style);
  if (!force && !fill && !border) return null;

  const rect = el.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;

  // CSS paints the border inside the border box, so the stroke centre line
  // sits half a border width in. The plate outline is the exception: that is
  // the glass edge and has to come out at its true outside size (233.0mm for a
  // 6 module, not 231.6mm), so the frame border is not allowed to inset it.
  const inset = outerEdge ? 0 : border ? border.widthMm / 2 : 0;
  const x = (rect.left - panelRect.left) * PX_TO_MM + inset;
  const y = (rect.top - panelRect.top) * PX_TO_MM + inset;
  const w = rect.width * PX_TO_MM - inset * 2;
  const h = rect.height * PX_TO_MM - inset * 2;
  if (w <= 0 || h <= 0) return null;

  const corners = [
    resolveRadius(style.getPropertyValue("border-top-left-radius"), w, h),
    resolveRadius(style.getPropertyValue("border-top-right-radius"), w, h),
    resolveRadius(style.getPropertyValue("border-bottom-right-radius"), w, h),
    resolveRadius(style.getPropertyValue("border-bottom-left-radius"), w, h),
  ];

  const points = simplifyPolyline(roundedRectPoints(x, y, w, h, corners));
  if (points.length < 2) return null;

  return {
    subpaths: [{ points, closed: true }],
    fill,
    fillOpacity: fill ? fill.alpha : 1,
    fillRule: "nonzero",
    stroke: border ? border.colour : null,
    strokeWidth: border ? border.widthMm : 0,
  };
};

/**
 * Measure the panel and return its artwork as polylines in millimetres.
 * The panel's own CSS transform is neutralised first so the geometry comes out
 * at the physical size the CSS declares (a 12 module panel is 240x160mm).
 *
 * `includeOutlines` adds the panel edge and every bordered box (module boxes,
 * button rings) so the file matches what is drawn on screen rather than the
 * icons alone.
 */
export const extractPanelGeometry = async (panelNode, { includeOutlines = true } = {}) => {
  const originalTransform = panelNode.style.transform;
  panelNode.style.transform = "none";

  let backgrounds = { cleanups: [], rasterCount: 0 };

  try {
    backgrounds = await injectVectorBackgrounds(panelNode);

    const panelRect = panelNode.getBoundingClientRect();
    const toPanelMm = (screenX, screenY) => [
      (screenX - panelRect.left) * PX_TO_MM,
      (screenY - panelRect.top) * PX_TO_MM,
    ];

    const roots = Array.from(panelNode.querySelectorAll("svg")).filter(
      // The MUI delete buttons are chrome, not artwork.
      (svgEl) => !svgEl.closest(".MuiSvgIcon-root, .MuiButtonBase-root")
    );

    // Painter's order: the coloured panel first, then the boxes drawn on it,
    // then the icon artwork on top.
    const shapes = [];
    let outlineCount = 0;
    let skippedText = 0;

    if (includeOutlines) {
      // The panel itself is always emitted, so the plate outline is in the
      // file even when it has no background colour set.
      const panelShape = outlineForElement(panelNode, panelRect, {
        force: true,
        outerEdge: true,
      });
      if (panelShape) {
        shapes.push(panelShape);
        outlineCount += 1;
      }

      for (const el of panelNode.querySelectorAll("*")) {
        if (el.dataset.laserExportTemp || el.closest("[data-laser-export-temp]")) {
          continue;
        }
        if (el.closest(".MuiSvgIcon-root, .MuiButtonBase-root")) continue;

        const outline = outlineForElement(el, panelRect);
        if (outline) {
          shapes.push(outline);
          outlineCount += 1;
        }
      }
    }

    for (const root of roots) {
      if (root.querySelector("text")) skippedText += 1;
      for (const el of root.querySelectorAll(GEOMETRY_TAGS.join(","))) {
        if (window.getComputedStyle(el).getPropertyValue("display") === "none") {
          continue;
        }
        const shape = flattenElement(el, toPanelMm);
        if (shape) shapes.push(shape);
      }
    }

    // getBoundingClientRect() rounds to device pixels, so a 24cm panel measures
    // 239.998mm. Snap the canvas back to the size the CSS actually declares.
    const snap = (value) => Math.round(value * 100) / 100;

    return {
      widthMm: snap(panelRect.width * PX_TO_MM),
      heightMm: snap(panelRect.height * PX_TO_MM),
      shapes,
      // Flat contour list, for formats that have no notion of a filled shape.
      polylines: shapes.flatMap((shape) => shape.subpaths),
      outlineCount,
      skippedText,
      skippedRasterBackgrounds: backgrounds.rasterCount,
    };
  } finally {
    backgrounds.cleanups.forEach((cleanup) => {
      try {
        cleanup();
      } catch (error) {
        console.warn("Failed to clean up background artwork", error);
      }
    });
    panelNode.style.transform = originalTransform;
  }
};

const subpathData = (subpath) =>
  subpath.points
    .map(
      (point, index) =>
        `${index === 0 ? "M" : "L"} ${fmt(point[0])} ${fmt(point[1])}`
    )
    .join(" ") + (subpath.closed ? " Z" : "");

/**
 * The whole panel as one plain SVG: real millimetre dimensions, absolute
 * coordinates, the panel colour, the outlines and the icon artwork, with the
 * fills and colours it has on screen - and not a single group or transform for
 * an importer to misread.
 *
 * `outlinesOnly` strips every fill and draws hairline contours instead, for
 * when the file is going straight to a cutter rather than being looked at.
 */
export const buildLaserSvg = (
  { widthMm, heightMm, shapes },
  { outlinesOnly = false } = {}
) => {
  const body = shapes
    .map((shape) => {
      const d = shape.subpaths.map(subpathData).join(" ");

      if (outlinesOnly) {
        return `  <path d="${d}" fill="none" stroke="#000000" stroke-width="0.1"/>`;
      }

      const attrs = [`d="${d}"`];
      attrs.push(shape.fill ? `fill="${shape.fill.hex}"` : 'fill="none"');
      if (shape.fill && shape.fillOpacity < 1) {
        attrs.push(`fill-opacity="${fmt(shape.fillOpacity)}"`);
      }
      if (shape.fillRule && shape.fillRule !== "nonzero") {
        attrs.push(`fill-rule="${shape.fillRule}"`);
      }
      if (shape.stroke && shape.strokeWidth > 0) {
        attrs.push(`stroke="${shape.stroke.hex}"`);
        attrs.push(`stroke-width="${fmt(shape.strokeWidth)}"`);
      }
      return `  <path ${attrs.join(" ")}/>`;
    })
    .join("\n");

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<svg xmlns="http://www.w3.org/2000/svg" version="1.1" width="${fmt(
      widthMm
    )}mm" height="${fmt(heightMm)}mm" viewBox="0 0 ${fmt(widthMm)} ${fmt(
      heightMm
    )}">`,
    body,
    "</svg>",
    "",
  ].join("\n");
};

// Fixed handles, following the usual AutoCAD layout. Entities are owned by the
// model space block record, which is always handle 1F.
const MODEL_SPACE = "1F";
const PAPER_SPACE = "1E";
const FIRST_ENTITY_HANDLE = 0x100;

/**
 * DXF AC1021 (AutoCAD 2007) with LWPOLYLINE entities - the same structure as
 * the Laser_KMT_SINGLE.dxf file that already imports into EZCAD, down to the
 * handles, subclass markers and full symbol tables.
 *
 * Y is flipped because CAD coordinates grow upwards while screen and SVG
 * coordinates grow downwards.
 */
export const buildLaserDxf = ({ widthMm, heightMm, polylines }) => {
  const out = [];
  const put = (code, value) => {
    out.push(String(code), String(value));
  };

  const toDxf = (point) => [point[0], heightMm - point[1]];
  const all = polylines.flatMap((polyline) => polyline.points.map(toDxf));
  const extMin = all.length
    ? [Math.min(...all.map((p) => p[0])), Math.min(...all.map((p) => p[1]))]
    : [0, 0];
  const extMax = all.length
    ? [Math.max(...all.map((p) => p[0])), Math.max(...all.map((p) => p[1]))]
    : [widthMm, heightMm];

  const handleSeed = (FIRST_ENTITY_HANDLE + polylines.length + 1).toString(16).toUpperCase();

  // --- HEADER ---------------------------------------------------------
  put(0, "SECTION");
  put(2, "HEADER");
  put(9, "$ACADVER");
  put(1, "AC1021");
  put(9, "$HANDSEED");
  put(5, handleSeed);
  put(9, "$INSUNITS");
  put(70, 4); // millimetres
  put(9, "$MEASUREMENT");
  put(70, 1); // metric
  put(9, "$LUNITS");
  put(70, 2); // decimal
  put(9, "$CLAYER");
  put(8, "0");
  put(9, "$EXTMIN");
  put(10, fmt(extMin[0]));
  put(20, fmt(extMin[1]));
  put(30, "0.0");
  put(9, "$EXTMAX");
  put(10, fmt(extMax[0]));
  put(20, fmt(extMax[1]));
  put(30, "0.0");
  put(0, "ENDSEC");

  put(0, "SECTION");
  put(2, "CLASSES");
  put(0, "ENDSEC");

  // --- TABLES ---------------------------------------------------------
  const emptyTable = (name, handle, extra) => {
    put(0, "TABLE");
    put(2, name);
    put(5, handle);
    put(330, "0");
    put(100, "AcDbSymbolTable");
    put(70, 0);
    if (extra) extra();
    put(0, "ENDTAB");
  };

  put(0, "SECTION");
  put(2, "TABLES");

  emptyTable("VPORT", "8");

  put(0, "TABLE");
  put(2, "LTYPE");
  put(5, "5");
  put(330, "0");
  put(100, "AcDbSymbolTable");
  put(70, 3);
  [
    ["14", "ByBlock"],
    ["15", "ByLayer"],
    ["16", "Continuous"],
  ].forEach(([handle, name]) => {
    put(0, "LTYPE");
    put(5, handle);
    put(330, "5");
    put(100, "AcDbSymbolTableRecord");
    put(100, "AcDbLinetypeTableRecord");
    put(2, name);
    put(70, 0);
    put(3, name === "Continuous" ? "Solid line" : "");
    put(72, 65);
    put(73, 0);
    put(40, "0.0");
  });
  put(0, "ENDTAB");

  put(0, "TABLE");
  put(2, "LAYER");
  put(5, "2");
  put(330, "0");
  put(100, "AcDbSymbolTable");
  put(70, 1);
  put(0, "LAYER");
  put(5, "10");
  put(330, "2");
  put(100, "AcDbSymbolTableRecord");
  put(100, "AcDbLayerTableRecord");
  put(2, "0");
  put(70, 0);
  put(62, 7);
  put(6, "Continuous");
  put(0, "ENDTAB");

  put(0, "TABLE");
  put(2, "STYLE");
  put(5, "3");
  put(330, "0");
  put(100, "AcDbSymbolTable");
  put(70, 1);
  put(0, "STYLE");
  put(5, "11");
  put(330, "3");
  put(100, "AcDbSymbolTableRecord");
  put(100, "AcDbTextStyleTableRecord");
  put(2, "Standard");
  put(70, 0);
  put(40, "0.0");
  put(41, "1.0");
  put(50, "0.0");
  put(71, 0);
  put(42, "2.5");
  put(3, "txt");
  put(4, "");
  put(0, "ENDTAB");

  emptyTable("VIEW", "6");
  emptyTable("UCS", "7");

  put(0, "TABLE");
  put(2, "APPID");
  put(5, "9");
  put(330, "0");
  put(100, "AcDbSymbolTable");
  put(70, 1);
  put(0, "APPID");
  put(5, "12");
  put(330, "9");
  put(100, "AcDbSymbolTableRecord");
  put(100, "AcDbRegAppTableRecord");
  put(2, "ACAD");
  put(70, 0);
  put(0, "ENDTAB");

  emptyTable("DIMSTYLE", "A", () => {
    put(100, "AcDbDimStyleTable");
    put(71, 0);
  });

  put(0, "TABLE");
  put(2, "BLOCK_RECORD");
  put(5, "1");
  put(330, "0");
  put(100, "AcDbSymbolTable");
  put(70, 2);
  [
    [MODEL_SPACE, "*Model_Space"],
    [PAPER_SPACE, "*Paper_Space"],
  ].forEach(([handle, name]) => {
    put(0, "BLOCK_RECORD");
    put(5, handle);
    put(330, "1");
    put(100, "AcDbSymbolTableRecord");
    put(100, "AcDbBlockTableRecord");
    put(2, name);
    put(70, 0);
  });
  put(0, "ENDTAB");
  put(0, "ENDSEC");

  // --- BLOCKS ---------------------------------------------------------
  put(0, "SECTION");
  put(2, "BLOCKS");
  [
    [MODEL_SPACE, "*Model_Space", "20", "21"],
    [PAPER_SPACE, "*Paper_Space", "1C", "1D"],
  ].forEach(([owner, name, blockHandle, endHandle]) => {
    put(0, "BLOCK");
    put(5, blockHandle);
    put(330, owner);
    put(100, "AcDbEntity");
    put(8, "0");
    put(100, "AcDbBlockBegin");
    put(2, name);
    put(70, 0);
    put(10, "0.0");
    put(20, "0.0");
    put(30, "0.0");
    put(3, name);
    put(1, "");
    put(0, "ENDBLK");
    put(5, endHandle);
    put(330, owner);
    put(100, "AcDbEntity");
    put(8, "0");
    put(100, "AcDbBlockEnd");
  });
  put(0, "ENDSEC");

  // --- ENTITIES -------------------------------------------------------
  put(0, "SECTION");
  put(2, "ENTITIES");
  polylines.forEach((polyline, index) => {
    put(0, "LWPOLYLINE");
    put(5, (FIRST_ENTITY_HANDLE + index).toString(16).toUpperCase());
    put(330, MODEL_SPACE);
    put(100, "AcDbEntity");
    put(8, "0");
    put(100, "AcDbPolyline");
    put(90, polyline.points.length);
    put(70, polyline.closed ? 1 : 0);
    put(43, "0.0"); // constant width
    polyline.points.forEach((point) => {
      const [x, y] = toDxf(point);
      put(10, fmt(x));
      put(20, fmt(y));
    });
  });
  put(0, "ENDSEC");

  // --- OBJECTS --------------------------------------------------------
  put(0, "SECTION");
  put(2, "OBJECTS");
  put(0, "DICTIONARY");
  put(5, "C");
  put(330, "0");
  put(100, "AcDbDictionary");
  put(281, 1);
  put(3, "ACAD_GROUP");
  put(350, "D");
  put(0, "DICTIONARY");
  put(5, "D");
  put(330, "C");
  put(100, "AcDbDictionary");
  put(281, 1);
  put(0, "ENDSEC");

  put(0, "EOF");

  return `${out.join("\r\n")}\r\n`;
};

/**
 * Measure the panel once and return both files, ready to hand to downloadjs.
 */
export const exportPanelVectors = async (panelNode, options) => {
  const geometry = await extractPanelGeometry(panelNode, options);
  return {
    svg: buildLaserSvg(geometry),
    dxf: buildLaserDxf(geometry),
    contours: geometry.polylines.length,
    outlineCount: geometry.outlineCount,
    widthMm: geometry.widthMm,
    heightMm: geometry.heightMm,
    skippedText: geometry.skippedText,
    skippedRasterBackgrounds: geometry.skippedRasterBackgrounds,
  };
};
